import React from "react";
import { ArrowRightLeft, FileInput, Cog, FileOutput, CheckCircle2, ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const steps = [
  { icon: FileInput, title: "Paper in", desc: "Faxes, scans, and mailed HCFA/UB-04 claims land in Hewto.ai — no format prep required." },
  { icon: Cog, title: "Claims capture & validate", desc: "Automated claims capture extracts every field, applies payer edits, and flags low-confidence data for review." },
  { icon: ArrowRightLeft, title: "Paper claim conversion to EDI", desc: "Captured claim data is converted into compliant 837P / 837I EDI transaction sets." },
  { icon: FileOutput, title: "EDI out", desc: "Ready-to-submit EDI is delivered to your clearinghouse or trading partners." },
];

const benefits = [
  "Eliminate manual keying of paper claims",
  "Reduce clearinghouse rejections with pre-validated data",
  "837P & 837I output mapped to payer requirements",
  "Full audit trail from paper source to EDI transaction",
  "SFTP, API, or webhook delivery",
  "Encrypted end-to-end with full audit trails",
];

const faqs = [
  {
    q: "What is healthcare claims capture?",
    a: "Healthcare claims capture is the process of digitizing paper-based claim forms — HCFA / CMS-1500, UB-04 / CMS-1450, and ADA J430D — and turning every field into structured, validated data. Hewto.ai automates claims capture end-to-end: fax and scan ingestion, layout-aware OCR, payer-edit validation, per-field confidence scoring, and downstream delivery to a clearinghouse or adjudication system.",
  },
  {
    q: "How does Hewto.ai automate paper claims capture and conversion to EDI?",
    a: "Hewto.ai automates paper claims capture and conversion in three steps: (1) ingest paper claims from fax, scan, digital mailroom, or SFTP, (2) capture every field with vision-language models fine-tuned on healthcare claim forms and validate the data against NPI / ICD-10 / CPT and payer-specific edits, (3) convert the captured data into a compliant 837P or 837I EDI transaction and deliver it to your clearinghouse — no manual keying at any step.",
  },
  {
    q: "What's the difference between claims capture and claims conversion?",
    a: "Claims capture is the extraction of every field from a paper claim form into structured, validated data. Claims conversion is the transformation of that structured data into an ANSI X12 EDI transaction (837P for professional or 837I for institutional). Hewto.ai does both in a single pipeline — claims capture plus paper-to-EDI conversion — so a scanned or faxed claim becomes a submission-ready 837 in minutes.",
  },
  {
    q: "What is paper to EDI conversion?",
    a: "Paper to EDI conversion is the process of turning paper-based healthcare claims — faxed or scanned HCFA / CMS-1500 and UB-04 / CMS-1450 forms — into compliant electronic EDI transactions (837P and 837I). Hewto.ai automates the entire paper to EDI workflow: claims capture, data validation, and EDI mapping.",
  },
  {
    q: "How does Hewto.ai automate paper to EDI?",
    a: "Hewto.ai ingests paper claims from fax, scan, or mail, uses AI-powered claims capture to extract every field, validates the data against payer edits, and maps it to compliant 837P / 837I EDI transaction sets — delivered to your clearinghouse via SFTP, API, or webhook with no manual keying.",
  },
  {
    q: "Which claim types are supported for claims capture and paper-to-EDI conversion?",
    a: "Both professional (HCFA / CMS-1500 → 837P) and institutional (UB-04 / CMS-1450 → 837I) claims are supported, along with the attachments and remittances that accompany them.",
  },
  {
    q: "Is the paper claims capture and conversion process accurate and auditable?",
    a: "Yes. Hewto.ai delivers 99%+ field-level accuracy on claims capture with per-field confidence scoring, optional human-in-the-loop review, and a full audit trail from the original paper source through to the final EDI transaction.",
  },
  {
    q: "Who uses Hewto.ai for claims capture and paper-to-EDI conversion?",
    a: "Health plans, TPAs, dental payers, hospitals, RCM providers, and clearinghouses use Hewto.ai for claims capture and paper-to-EDI conversion. Common use cases: replacing offshore keying teams, eliminating fax-to-manual-entry workflows, and building an automated claims intake pipeline for HCFA, UB-04, and ADA J430D claim forms.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const PaperToEDI = () => (
  <PageTransition>
    <Seo
      title="Paper Claims Capture & Conversion to 837 EDI | Hewto.ai"
      description="Hewto.ai automates paper claims capture and paper-to-EDI conversion — turning faxed and scanned HCFA and UB-04 claim forms into compliant 837P and 837I EDI transactions. End-to-end claims capture, claims conversion, and paper-to-electronic claim conversion for healthcare payers, providers, and clearinghouses."
      keywords="claims capture, paper claims capture, healthcare claims capture, claims data capture, claim capture software, digital claims capture, automated claims capture, conversion, paper claim conversion, claims conversion, paper to edi conversion, paper claim to electronic conversion, paper claims to 837 conversion, claim conversion software, paper to edi, paper to edi conversion, paper claims to 837 edi, 837p conversion, 837i conversion, healthcare edi conversion, hcfa to edi conversion, ub-04 to edi conversion, medical claims capture, electronic claims capture, ocr claims capture, claims intake automation"
      canonical="https://www.hewto.ai/paper-to-edi-service"
      jsonLd={faqJsonLd}
    />
    <PageHero
      eyebrow="Products · Paper to EDI"
      title="Paper claims capture & conversion to 837 EDI"
      subtitle="Hewto.ai automates paper claims capture and paper-to-EDI conversion — turning faxed and scanned HCFA / CMS-1500 and UB-04 / CMS-1450 forms into compliant 837 EDI transactions. End-to-end claims capture and claim conversion for payers, providers, and clearinghouses — no manual keying, fully validated, audit-ready."
    />

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            The pipeline
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Four steps, zero manual entry
          </h2>
          <p className="mt-4 leading-relaxed text-slate-600">
            Hewto.ai handles the full paper to EDI conversion pipeline — from paper claim intake to
            validated 837 EDI output.
          </p>
        </div>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.1}>
            <div className="relative rounded-2xl border border-slate-200 bg-white p-8">
              <span className="absolute right-6 top-6 text-5xl font-bold text-slate-100">{i + 1}</span>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand text-white">
                <s.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <div className="mb-10 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Why teams switch to Hewto.ai
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {benefits.map((b, i) => (
            <Reveal key={b} delay={(i % 2) * 0.06}>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                <span className="text-slate-700">{b}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          Paper to EDI FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Paper to EDI conversion, answered
        </h2>
        <p className="mt-4 leading-relaxed text-slate-600">
          Common questions about automating paper to EDI conversion for HCFA and UB-04 claims with
          Hewto.ai.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="paper-edi-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`paper-edi-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default PaperToEDI;
